import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BadgeAlert, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

function money(value, currency = 'UGX') {
  return `${currency} ${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function currentMonth() {
  return String(new Date().getMonth() + 1);
}

function currentYear() {
  return String(new Date().getFullYear());
}

function statusClass(status) {
  const value = String(status || '').toLowerCase();
  if (value === 'safe') return 'bg-emerald-100 text-emerald-800';
  if (value === 'warning') return 'bg-amber-100 text-amber-800';
  return 'bg-red-100 text-red-800';
}

export default function BudgetBudgets() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({
    month: currentMonth(),
    year: currentYear(),
    branch_id: 'all',
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    category_id: '',
    limit_amount: '',
    branch_id: 'all',
    month: '',
    year: '',
    currency: user?.default_currency || 'UGX',
  });

  const load = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        month: nextFilters.month || undefined,
        year: nextFilters.year || undefined,
      };
      if (nextFilters.branch_id && nextFilters.branch_id !== 'all') params.branch_id = nextFilters.branch_id;
      const res = await api.get('/budget/budgets', { params });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to load budget limits');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const branches = useMemo(() => Array.isArray(data?.branches) ? data.branches : [], [data?.branches]);
  const expenseCategories = useMemo(() => Array.isArray(data?.expense_categories) ? data.expense_categories : [], [data?.expense_categories]);
  const activeBranches = useMemo(() => branches.filter((branch) => String(branch?.status || 'active').toLowerCase() === 'active'), [branches]);
  const singleBranch = activeBranches.length <= 1 || !!data?.single_branch;
  const budgets = Array.isArray(data?.budgets) ? data.budgets : [];
  const summary = data?.summary || {};
  const currentCurrency = user?.default_currency || 'UGX';

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    load(next);
  };

  const resetForm = () => {
    setForm({
      category_id: expenseCategories[0]?.id || '',
      limit_amount: '',
      branch_id: singleBranch ? (activeBranches[0]?.id || branches[0]?.id || 'all') : (filters.branch_id || 'all'),
      month: filters.month || '',
      year: filters.year || '',
      currency: user?.default_currency || 'UGX',
    });
  };

  const openCreate = () => {
    setEditing(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (budget) => {
    setEditing(budget);
    setForm({
      category_id: budget.category_id || '',
      limit_amount: budget.monthly_budget ?? budget.limit_amount ?? '',
      branch_id: budget.branch_id || (singleBranch ? (activeBranches[0]?.id || branches[0]?.id || 'all') : 'all'),
      month: budget.month || filters.month || '',
      year: budget.year || filters.year || '',
      currency: budget.currency || user?.default_currency || 'UGX',
    });
    setOpen(true);
  };

  const saveBudget = async () => {
    if (!form.category_id) return toast.error('Please select an expense category.');
    if (Number(form.limit_amount || 0) <= 0) return toast.error('Amount must be greater than zero.');
    setSaving(true);
    try {
      const payload = {
        category_id: form.category_id,
        limit_amount: Number(form.limit_amount || 0),
        branch_id: singleBranch ? (activeBranches[0]?.id || branches[0]?.id || form.branch_id || 'all') : (form.branch_id || 'all'),
        month: form.month || undefined,
        year: form.year || undefined,
        currency: form.currency || currentCurrency,
      };
      if (editing?.id) {
        await api.put(`/budget/budgets/${editing.id}`, payload);
        toast.success('Budget updated');
      } else {
        await api.post('/budget/budgets', payload);
        toast.success('Budget created');
      }
      setOpen(false);
      setEditing(null);
      resetForm();
      await load(filters);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to save budget');
    } finally {
      setSaving(false);
    }
  };

  const deleteBudget = async (budget) => {
    if (!window.confirm('Delete this budget?')) return;
    try {
      await api.delete(`/budget/budgets/${budget.id}`);
      toast.success('Budget deleted');
      await load(filters);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to delete budget');
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (data?.upgrade_required) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <BadgeAlert size={22} className="text-emerald-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Budgets</h1>
              <p className="text-sm text-slate-500">Upgrade your package to unlock Budget and Financial Intelligence.</p>
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
            {data?.message || 'This feature is available on premium packages only.'}
          </div>
          <div className="flex gap-2">
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => navigate('/dashboard/package')}>
              View Packages
            </Button>
            <Button variant="outline" className="border-slate-200" onClick={load}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalMonthlyBudget = Number(summary.total_monthly_budget || 0);
  const spentThisMonth = Number(summary.spent_this_month || 0);
  const remainingBudget = Number(summary.remaining_budget || 0);
  const categoriesExceeded = Number(summary.categories_exceeded || 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Budgets
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Set monthly spending limits per category and track category health.</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={openCreate}>
          <Plus size={16} className="mr-1.5" /> Set Budget
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Total Monthly Budget</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money(totalMonthlyBudget, currentCurrency)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Spent This Month</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money(spentThisMonth, currentCurrency)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Remaining Budget</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money(remainingBudget, currentCurrency)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Categories Exceeded</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{categoriesExceeded}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="space-y-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Filters</CardTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <Input type="number" min="1" max="12" value={filters.month} onChange={(e) => updateFilter('month', e.target.value)} placeholder="Month" className="bg-white" />
            <Input type="number" min="2020" max="2100" value={filters.year} onChange={(e) => updateFilter('year', e.target.value)} placeholder="Year" className="bg-white" />
            {!singleBranch ? (
              <Select value={filters.branch_id} onValueChange={(value) => updateFilter('branch_id', value)}>
                <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-600">
                {activeBranches[0]?.name || 'Main Branch'}
              </div>
            )}
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={`${filters.month}/${filters.year}`} readOnly className="pl-9 bg-slate-50 text-slate-500" />
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Budget List</CardTitle>
          <Badge variant="outline" className="border-slate-200 text-slate-600 bg-slate-50">{budgets.length} item(s)</Badge>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="py-3 pr-4 font-medium">Category</th>
                  <th className="py-3 pr-4 font-medium">Monthly Budget</th>
                  <th className="py-3 pr-4 font-medium">Spent This Month</th>
                  <th className="py-3 pr-4 font-medium">Remaining</th>
                  <th className="py-3 pr-4 font-medium">Percentage Used</th>
                  <th className="py-3 pr-4 font-medium">Status Badge</th>
                  <th className="py-3 pr-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {budgets.map((budget) => (
                  <tr key={budget.id || `${budget.category_id}-${budget.month}-${budget.year}`} className="border-b border-slate-50 last:border-0">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-slate-900">{budget.category_name}</div>
                      <div className="text-xs text-slate-500">{budget.branch_name || 'All Branches'}</div>
                    </td>
                    <td className="py-3 pr-4 text-slate-700">{money(budget.monthly_budget, budget.currency || currentCurrency)}</td>
                    <td className="py-3 pr-4 text-slate-700">{money(budget.spent_this_month, budget.currency || currentCurrency)}</td>
                    <td className="py-3 pr-4 text-slate-700">{money(budget.remaining, budget.currency || currentCurrency)}</td>
                    <td className="py-3 pr-4 text-slate-700">{Number(budget.percentage_used || 0).toFixed(0)}%</td>
                    <td className="py-3 pr-4">
                      <Badge className={statusClass(budget.status)}>{budget.status || 'safe'}</Badge>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap gap-2">
                        {budget.id ? (
                          <>
                            <Button size="sm" variant="outline" className="border-slate-200" onClick={() => openEdit(budget)}>Edit</Button>
                            <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deleteBudget(budget)}>
                              <Trash2 size={14} />
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => { setEditing(null); setForm((prev) => ({ ...prev, category_id: budget.category_id, limit_amount: '', branch_id: budget.branch_id || 'all', month: filters.month, year: filters.year })); setOpen(true); }}>
                            Set Budget
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {budgets.length === 0 && (
                  <tr>
                    <td className="py-8 text-center text-slate-500" colSpan={7}>No budget categories found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Budget' : 'Set Budget'}</DialogTitle>
            <DialogDescription>Set a monthly spending limit for an expense category.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Expense Category</Label>
              <Select value={form.category_id} onValueChange={(value) => setForm((prev) => ({ ...prev, category_id: value }))}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {expenseCategories.map((category) => <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Monthly Limit</Label>
              <Input type="number" value={form.limit_amount} onChange={(e) => setForm((prev) => ({ ...prev, limit_amount: e.target.value }))} />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {!singleBranch ? (
                <div className="grid gap-2">
                  <Label>Branch (optional)</Label>
                  <Select value={form.branch_id} onValueChange={(value) => setForm((prev) => ({ ...prev, branch_id: value }))}>
                    <SelectTrigger><SelectValue placeholder="All Branches" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {branches.map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <div className="grid gap-2">
                <Label>Month (optional)</Label>
                <Input type="number" min="1" max="12" value={form.month} onChange={(e) => setForm((prev) => ({ ...prev, month: e.target.value }))} placeholder={currentMonth()} />
              </div>
              <div className="grid gap-2">
                <Label>Year (optional)</Label>
                <Input type="number" min="2020" max="2100" value={form.year} onChange={(e) => setForm((prev) => ({ ...prev, year: e.target.value }))} placeholder={currentYear()} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={saveBudget} disabled={saving}>
              {saving ? 'Saving...' : 'Save Budget'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
